import { useNavigate, useParams, Link } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { type CreateJobInput } from '@ats/shared'
import { jobsApi } from '@/lib/api'
import { PageLayout } from '@/components/layout/PageLayout'
import { Button } from '@/components/ui/button'
import { JobForm } from '@/components/features/jobs/JobForm'
import { toast } from '@/components/ui/toaster'

export function EditJobPage() {
  const { jobId } = useParams({ from: '/_app/jobs/$jobId/edit' })
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: job, isLoading } = useQuery({
    queryKey: ['job', jobId],
    queryFn: () => jobsApi.get(jobId),
  })

  const updateJob = useMutation({
    mutationFn: (data: CreateJobInput) => jobsApi.update(jobId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] })
      queryClient.invalidateQueries({ queryKey: ['job', jobId] })
      toast({ title: 'Job updated successfully', variant: 'success' })
      navigate({ to: `/jobs/${jobId}` })
    },
    onError: (err) => {
      toast({
        title: 'Failed to update job',
        description: err instanceof Error ? err.message : 'Something went wrong',
        variant: 'destructive',
      })
    },
  })

  const handleSubmit = async (data: CreateJobInput) => {
    await updateJob.mutateAsync(data)
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 skeleton" />
        <div className="h-96 skeleton rounded-lg" />
      </div>
    )
  }

  if (!job) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold">Job not found</h2>
        <Button variant="link" asChild className="mt-4">
          <Link to="/jobs">Back to jobs</Link>
        </Button>
      </div>
    )
  }

  const jobData = job as any

  return (
    <PageLayout fullHeight back={`/jobs/${jobId}`} title="Edit Job">
      <JobForm
        initialData={{
          title: jobData.title,
          description: jobData.description,
          requirements: jobData.requirements,
          benefits: jobData.benefits,
          departmentId: jobData.departmentId,
          locationId: jobData.locationId,
          employmentType: jobData.employmentType,
          experienceLevel: jobData.experienceLevel,
          salaryMin: jobData.salaryMin,
          salaryMax: jobData.salaryMax,
          salaryCurrency: jobData.salaryCurrency,
          salaryPeriod: jobData.salaryPeriod,
          showSalary: jobData.showSalary,
          remotePolicy: jobData.remotePolicy,
          hiringManagerId: jobData.hiringManagerId,
        }}
        onSubmit={handleSubmit}
        isLoading={updateJob.isPending}
        submitLabel="Save Changes"
      />
    </PageLayout>
  )
}
